import { FlightResponse } from '@/types/benefits/flight/FlightResponse';
import { CURRENCIES } from '../../../../../shared/CountriesData';
import { LOCALES } from '@/i18n/config';
import { SupportedLocale } from '@/types/supported-locale.type';
import { numberIfNotUndefined } from '../../prices/numberIfNotUndefined';

// Helper function to initialize translations structure for Flight
const initializeFlightTranslations = (flight: FlightResponse) => {
  const translations: Record<
    SupportedLocale,
    {
      shortDescription?: string;
      longDescription?: string;
      benefit?: { name?: string };
    }
  > = {} as Record<
    SupportedLocale,
    {
      shortDescription?: string;
      longDescription?: string;
      benefit?: { name?: string };
    }
  >;

  for (const locale of LOCALES) {
    translations[locale] = {
      // Flight direct translatable fields
      shortDescription:
        flight.translations?.[locale]?.shortDescription ||
        flight.shortDescription ||
        '',
      longDescription:
        flight.translations?.[locale]?.longDescription ||
        flight.longDescription ||
        '',
      // Nested benefit translatable fields
      benefit: {
        name:
          flight.translations?.[locale]?.benefit?.name ||
          flight.benefit?.name ||
          '',
      },
    };
  }

  return translations;
};

export const loadFlightDefaultValues = (flight: FlightResponse) =>
  ({
    name: flight.benefit.name,
    supplier: {
      label: flight.benefit.supplier.name,
      value: flight.benefit.supplier.id,
    },
    currency: {
      value: flight.benefit.currency,
      label:
        CURRENCIES.find(
          (currency) => currency.value === flight.benefit.currency,
        )?.label ?? '',
    },
    marginRate: numberIfNotUndefined(flight.benefit.marginRate),
    sourceCountry: flight.sourceCountry
      ? { label: flight.sourceCountry, value: flight.sourceCountry }
      : undefined,
    sourceCity: flight.sourceCity
      ? { label: flight.sourceCity, value: flight.sourceCity }
      : undefined,
    sourceAirport: flight.sourceAirport
      ? { label: flight.sourceAirport, value: flight.sourceAirport }
      : undefined,
    destinationCountry: flight.destinationCountry
      ? { label: flight.destinationCountry, value: flight.destinationCountry }
      : undefined,
    destinationCity: flight.destinationCity
      ? { label: flight.destinationCity, value: flight.destinationCity }
      : undefined,
    destinationAirport: flight.destinationAirport
      ? { label: flight.destinationAirport, value: flight.destinationAirport }
      : undefined,
    shortDescription: flight.shortDescription ?? undefined,
    longDescription: flight.longDescription ?? undefined,
    translations: initializeFlightTranslations(flight),
    tarifType: 'par-personne',
    customTarif: flight.specialTarif ? 'true' : 'false',
    childAgeFrom: flight.childAgeFrom ? Number(flight.childAgeFrom) : undefined,
    childAgeTo: flight.childAgeTo ? Number(flight.childAgeTo) : undefined,
    tarifs:
      flight.benefit.Pricing && flight.benefit.Pricing.length > 0
        ? flight.benefit.Pricing.map((pricing) => ({
            name: pricing.name ?? undefined,
            periods: pricing.periods.map((period) => ({
              startDate: period.startDate
                ? new Date(period.startDate)
                : undefined,
              endDate: period.endDate ? new Date(period.endDate) : undefined,
              applicationDays: period.applicationDays,
            })),
            pricePartitions: pricing.pricePartitions.map((partition) => ({
              price: numberIfNotUndefined(partition.price),
              specialPrice: numberIfNotUndefined(partition.specialPrice),
              from: 1,
              to: 1,
            })),
          }))
        : [
            {
              periods: [{ applicationDays: [] }],
              pricePartitions: [
                {
                  from: 1,
                  to: 1,
                },
              ],
            },
          ],
  }) as const;
